/**
 * Shared path-tree helpers extracted from JsonPeekPopover so the same
 * tree-building / wildcard-collapsing logic can power the
 * `JsonShapePreview` component too. Kept as a sibling util (rather than
 * exported off the popover) so the dependency direction stays clear:
 * both consumers depend on this module, not on each other.
 */

import {
  Braces,
  Brackets,
  Hash,
  Slash,
  ToggleLeft,
  Type,
  type LucideIcon,
} from 'lucide-react';
import type {
  JsonStructureCachePath,
  JsonStructureCachePathType,
} from '@/types/electron';

/** One node in the path tree. Interior nodes that weren't observed (rare,
 * but older cache entries may lack them) keep `type`/`prevalence`
 * undefined so the row renders without those columns. */
export type PathTreeNode = {
  /** Trailing segment displayed in the row (e.g. `frame`, `[*]`). */
  segment: string;
  /** Full JSON path (e.g. `$.frame.material`). */
  path: string;
  type: JsonStructureCachePathType | undefined;
  prevalence: number | undefined;
  children: PathTreeNode[];
};

/**
 * Split a JSONPath into segments while keeping array steps (`[*]`,
 * `[0]`) as their own tokens. We strip a leading `$` so the root is
 * implicit — the tree is rendered as a list of root children.
 */
export function parsePathSegments(path: string): string[] {
  const segments: string[] = [];
  let cursor = 0;
  if (path.startsWith('$')) cursor = 1;
  let current = '';
  while (cursor < path.length) {
    const ch = path[cursor];
    if (ch === '.') {
      if (current) {
        segments.push(current);
        current = '';
      }
      cursor++;
    } else if (ch === '[') {
      if (current) {
        segments.push(current);
        current = '';
      }
      const end = path.indexOf(']', cursor);
      if (end === -1) break;
      segments.push(path.substring(cursor, end + 1));
      cursor = end + 1;
    } else {
      current += ch;
      cursor++;
    }
  }
  if (current) segments.push(current);
  return segments;
}

/** Build a path tree from the flat structure-cache list. */
export function buildPathTree(
  paths: ReadonlyArray<JsonStructureCachePath>,
): PathTreeNode {
  const root: PathTreeNode = {
    segment: '$',
    path: '$',
    type: undefined,
    prevalence: undefined,
    children: [],
  };
  const sorted = [...paths].sort(
    (a, b) => parsePathSegments(a.path).length - parsePathSegments(b.path).length,
  );
  for (const p of sorted) {
    const segments = parsePathSegments(p.path);
    let current = root;
    let accumulated = '$';
    for (const seg of segments) {
      const childPath = seg.startsWith('[') ? `${accumulated}${seg}` : `${accumulated}.${seg}`;
      let next = current.children.find((c) => c.segment === seg);
      if (!next) {
        next = {
          segment: seg,
          path: childPath,
          type: undefined,
          prevalence: undefined,
          children: [],
        };
        current.children.push(next);
      }
      current = next;
      accumulated = childPath;
    }
    current.type = p.type;
    current.prevalence = p.prevalence;
  }
  const sortChildren = (node: PathTreeNode): void => {
    node.children.sort((a, b) => {
      const ap = a.prevalence ?? -1;
      const bp = b.prevalence ?? -1;
      if (ap !== bp) return bp - ap;
      return a.segment.localeCompare(b.segment);
    });
    for (const c of node.children) sortChildren(c);
  };
  sortChildren(root);
  return collapseArrayWildcards(root);
}

/**
 * Fold an array container into its `[*]` child when the child is the only
 * child. Resulting segment carries the `[*]` suffix (e.g. `features[*]`),
 * which is the path users actually filter against. Applies recursively, so
 * nested arrays (`matrix[*][*]`) collapse into a single segment too.
 */
export function collapseArrayWildcards(node: PathTreeNode): PathTreeNode {
  const mergedChildren = node.children.map(collapseArrayWildcards);
  if (mergedChildren.length === 1 && mergedChildren[0].segment.startsWith('[')) {
    const w = mergedChildren[0];
    return {
      segment: node.segment === '$' ? w.segment : `${node.segment}${w.segment}`,
      path: w.path,
      type: w.type ?? node.type,
      prevalence: w.prevalence ?? node.prevalence,
      children: w.children,
    };
  }
  return { ...node, children: mergedChildren };
}

/** Map an observed path type to a small monochrome lucide icon. */
export const TYPE_ICONS: Record<JsonStructureCachePathType, LucideIcon> = {
  string: Type,
  number: Hash,
  boolean: ToggleLeft,
  object: Braces,
  array: Brackets,
  null: Slash,
  mixed: Slash,
};

/** Human-readable label for the per-row type glyph. */
export const TYPE_LABELS: Record<JsonStructureCachePathType, string> = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  object: 'object',
  array: 'array',
  null: 'null',
  mixed: 'mixed',
};

/** Shared percent formatter so both surfaces render the same `50%`-style. */
export const jsonPercentFormatter = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 0,
});
